import { CobaltCheckbox } from '@cae/cobalt-react/components/checkbox';
import { CobaltDivider } from '@cae/cobalt-react/components/divider';
import {
  CobaltFormField,
  CobaltFormFieldHelperText,
  CobaltFormFieldLabel,
} from '@cae/cobalt-react/components/form-field';
import { CobaltLoaderCircular } from '@cae/cobalt-react/components/loader-circular';
import { CobaltTextInput } from '@cae/cobalt-react/components/text-input';
import { CobaltToggle } from '@cae/cobalt-react/components/toggle';
import * as React from 'react';

import type { UserFormFieldsProps } from './UserFormFields.types';

import { ROLE_OPTIONS } from '../../UserDetailPanel.utils';
import styles from './UserFormFields.module.css';

const UserFormFields = React.memo(
  ({
    mode,
    formData,
    errors,
    onFieldChange,
    onRoleToggle,
    onStatusChange,
    isPersonalInfoDisabled,
    isPermissionsDisabled,
    isCheckingEmail,
    onEmailBlur,
    onEmailFocus,
    personalInfoNote,
  }: Readonly<UserFormFieldsProps>): React.ReactElement => {
    return (
      <div data-testid="user-form-fields" className={styles.userFormFields}>
        {mode !== 'add' && mode !== 'sf-add' && (
          <section
            className={styles.usersStatusSection}
            aria-labelledby="user-status-title"
          >
            <div className={styles.usersStatusSectionContent}>
              <h3
                className={`body-02 ${styles.usersStatusTitle}`}
                id="user-status-title"
              >
                User status
              </h3>
              <p className={`body-01 ${styles.usersStatusDescription}`}>
                Work status within the organization
              </p>
            </div>

            <CobaltToggle
              checked={formData.status === 'Active'}
              labelText={formData.status === 'Active' ? 'Active' : 'Inactive'}
              disabled={isPermissionsDisabled}
              onChange={onStatusChange}
              labelPosition="left"
            />
          </section>
        )}

        <section className={styles.formSection}>
          <h3 className={`heading-01 ${styles.sectionTitle}`}>
            Personal information
          </h3>

          {personalInfoNote}

          <CobaltFormField
            id="firstName"
            error={errors.firstName}
            disabled={isPersonalInfoDisabled}
          >
            <CobaltFormFieldLabel htmlFor="firstName" label="First name" />
            <CobaltTextInput
              id="firstName"
              value={formData.firstName}
              disabled={isPersonalInfoDisabled}
              onChange={e => onFieldChange('firstName', e.target.value)}
              autoComplete="new-password"
            />
            <CobaltFormFieldHelperText
              helperId="firstName-helper"
              error={errors.firstName}
            />
          </CobaltFormField>

          <CobaltFormField
            id="middleName"
            error={errors.middleName}
            disabled={isPersonalInfoDisabled}
          >
            <CobaltFormFieldLabel
              htmlFor="middleName"
              label="Middle name (optional)"
            />
            <CobaltTextInput
              id="middleName"
              value={formData.middleName}
              disabled={isPersonalInfoDisabled}
              onChange={e => onFieldChange('middleName', e.target.value)}
              autoComplete="new-password"
            />
            <CobaltFormFieldHelperText
              helperId="middleName-helper"
              error={errors.middleName}
            />
          </CobaltFormField>

          <CobaltFormField
            id="lastName"
            error={errors.lastName}
            disabled={isPersonalInfoDisabled}
          >
            <CobaltFormFieldLabel htmlFor="lastName" label="Last name" />
            <CobaltTextInput
              id="lastName"
              value={formData.lastName}
              disabled={isPersonalInfoDisabled}
              onChange={e => onFieldChange('lastName', e.target.value)}
              autoComplete="new-password"
            />
            <CobaltFormFieldHelperText
              helperId="lastName-helper"
              error={errors.lastName}
            />
          </CobaltFormField>

          <CobaltFormField
            id="email"
            error={errors.email}
            disabled={isPersonalInfoDisabled}
            suffix={
              isCheckingEmail ? (
                <span className={styles.emailLoader}>
                  <CobaltLoaderCircular />
                </span>
              ) : undefined
            }
          >
            <CobaltFormFieldLabel htmlFor="email" label="Email" />
            <CobaltTextInput
              id="email"
              value={formData.email}
              disabled={isPersonalInfoDisabled}
              onChange={e => onFieldChange('email', e.target.value)}
              onBlur={onEmailBlur}
              onFocus={onEmailFocus}
              autoComplete="new-password"
            />
            <CobaltFormFieldHelperText
              helperId="email-helper"
              error={errors.email}
            />
          </CobaltFormField>
        </section>

        <section className={styles.formSection}>
          <h3 className={`heading-01 ${styles.sectionTitle}`}>Role</h3>

          <div className={styles.rolesGroup} role="group" aria-label="Roles">
            {ROLE_OPTIONS.map(option => (
              <React.Fragment key={option.value}>
                <CobaltCheckbox
                  labelText={option.label}
                  description={option.description}
                  checked={formData.roles.includes(option.value)}
                  disabled={isPermissionsDisabled}
                  onChange={() => onRoleToggle(option.value)}
                  isInvalid={!!errors.roles}
                />
                {option.value === 'Admin' && <CobaltDivider color="subtle" />}
              </React.Fragment>
            ))}
          </div>
          {errors.roles && (
            <p className={`${styles.rolesError} body-01`} role="alert">
              {errors.roles}
            </p>
          )}
        </section>
      </div>
    );
  }
);

UserFormFields.displayName = 'UserFormFields';

export { UserFormFields };
